import Vue from 'vue'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'

// Reglas de edición de una matriz de permisos (módulo × acción), compartidas entre la
// pantalla de defaults (permission_default) y la futura edición por worker
// (worker_permission_override, Organization → Workers → Edit → {id}) — mismas reglas,
// mismo código, sin duplicar. Cada función recibe el `doc` reactivo ya resuelto (el
// documento puntual a editar — un privilegio o un worker) y un `target` ({ collection,
// query }) que dice dónde persistir. Ningún checkbox está bloqueado por lógica de negocio:
// cualquier combinación es settable, incluidas las que hoy no tienen sentido para un
// privilegio o worker puntual — quien decide eso es quien edita, no el código.
export default function usePermissionMatrix() {
  const { updateItem, getItemAndUpdate, deleteItem } = useLambda()
  const { showErrorMessage } = useNotifications()

  // 'all' | 'own' | null (view not allowed at all) — única fuente de verdad del estado de
  // Ver de una celda, compartida con los templates que la consuman.
  const viewMode = cell => {
    if (!cell.view || !cell.view.allowed) return null
    return cell.view.scope === 'own' ? 'own' : 'all'
  }

  // Persiste un valor puntual (módulo.acción) sobre `target` — actualización optimista
  // con revert automático si falla el guardado. Vue.set es necesario: create/export/delete
  // (y a veces edit) pueden no existir todavía en el documento, así que muchas veces esto
  // agrega una propiedad nueva, que Vue 2 no vuelve reactiva con una asignación simple.
  // `target.upsert: true` (worker_permission_override) crea el documento si todavía no
  // existe — la mayoría de los workers no tienen override hasta el primer cambio. Mismo
  // patrón que ya usa el código para la colección `counter` (getItemAndUpdate + upsert).
  const updateCell = async (doc, target, moduleKey, action, value) => {
    if (!doc.modules) Vue.set(doc, 'modules', {})

    const previous = doc.modules[moduleKey]?.[action] || null
    if (!doc.modules[moduleKey]) Vue.set(doc.modules, moduleKey, {})
    Vue.set(doc.modules[moduleKey], action, value)

    try {
      const persistedAction = { $set: { [`modules.${moduleKey}.${action}`]: value } }

      if (target.upsert) {
        await getItemAndUpdate({
          collection: target.collection,
          query: target.query,
          action: persistedAction,
          options: { upsert: true },
        })
      } else {
        await updateItem({ collection: target.collection, query: target.query, action: persistedAction })
      }
      // Dev-only confirmation that the change ran and persisted — remove once no longer needed.
      // `value` was just Vue.set into the reactive doc, so logging it directly prints the
      // reactive Observer wrapper instead of a plain object — stringify to keep it readable.
      console.log(`[${target.collection}] ${JSON.stringify(target.query)} · ${moduleKey}.${action} → ${JSON.stringify(value)}`)
    } catch (error) {
      console.log(error)
      Vue.set(doc.modules[moduleKey], action, previous)
      showErrorMessage(i18n.t('message.permission_update_error'))
    }
  }

  // Borra el documento entero de `target` — "volver a heredar todo del default". Usado por
  // el botón "Restablecer a valores por defecto" y por el cambio de privilegio confirmado
  // en la edición de worker. No hace falta upsert acá: si el documento no existía, borrar
  // no hace nada (no es un error).
  const resetTarget = async target => {
    try {
      await deleteItem({ collection: target.collection, query: target.query })
      return true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.permission_update_error'))
      return false
    }
  }

  // "Ver todo" / "Ver lo propio" son mutuamente excluyentes por construcción. Clickear la
  // opción ya activa apaga Ver del todo; clickear la otra cambia a esa.
  // Editar nunca tiene alcance propio: siempre hereda el de Ver, así que si Ver se apaga
  // del todo, Editar no puede seguir habilitado — se apaga junto con él.
  // `effectiveCell` (opcional) es el valor REAL que ve el usuario para este módulo — en la
  // pantalla de defaults es el mismo `doc`, pero en la de overrides por worker, Editar puede
  // estar heredado del default (no presente todavía en `doc`, el documento de override) y
  // aun así hay que apagarlo. Sin este parámetro, cae a leer de `doc` (comportamiento previo).
  const setViewMode = (doc, target, moduleKey, mode, effectiveCell) => {
    let value = { allowed: false }
    if (mode === 'own') value = { allowed: true, scope: 'own' }
    else if (mode === 'all') value = { allowed: true }

    updateCell(doc, target, moduleKey, 'view', value)

    const currentEdit = (effectiveCell || doc.modules?.[moduleKey] || {}).edit
    if (mode === null && currentEdit?.allowed) {
      updateCell(doc, target, moduleKey, 'edit', { allowed: false })
    }
  }

  // Toggle simple on/off para edit/create/export/delete. Editar nunca guarda su propio
  // scope — su alcance efectivo siempre es el de Ver (reglas documentadas en
  // src/docs/permisos-plan.md). Si se habilita Editar sin ningún Ver seleccionado, se
  // asume "Ver todo" automáticamente: nadie puede editar lo que no puede visualizar, y si
  // en realidad se quería "propio" se cambia con un click más. `cell` ya es el valor
  // efectivo (lo pasa el caller), así que esto funciona igual para ambas pantallas.
  const toggleAction = (doc, target, moduleKey, action, cell) => {
    const isAllowed = !!(cell[action] && cell[action].allowed)
    updateCell(doc, target, moduleKey, action, { allowed: !isAllowed })

    if (action === 'edit' && !isAllowed && viewMode(cell) === null) {
      setViewMode(doc, target, moduleKey, 'all', cell)
    }
  }

  return {
    viewMode,
    updateCell,
    setViewMode,
    toggleAction,
    resetTarget,
  }
}
